import {Injectable} from '@angular/core';

import {StorageService} from "./storage.service";
import mqtt, {IClientOptions} from 'mqtt';
import {Subject} from "rxjs";
import {Buffer} from "buffer";

class DeviceSubscribeItem {
  sn: string;
  deviceEventSource = new Subject<DeviceRemoteEvent>();
  deviceEvent$ = this.deviceEventSource.asObservable();
}

class DeviceDataUpdateMqttEvent {
  event: string = 'data_update'
  ts: string = new Date().toISOString();
  sn: string = null;
  data: any = null;
}

class DeviceTaskResponseMqttEvent {
  event: string = 'task_response'
  ts: string = new Date().toISOString();
  sn: string = null
  data: {
    taskId?: string
    status?: number
  } = {}
}

export type DeviceRemoteEvent = DeviceDataUpdateMqttEvent | DeviceTaskResponseMqttEvent;

@Injectable({
  providedIn: 'root'
})
export class MqttService {
  private readonly TAG = '[MqttService]'

  public client: mqtt.MqttClient;
  private topicMap = new Map<string, DeviceSubscribeItem>();
  private readonly deviceEventTopicPrefix = 'mp/device/event/'


  constructor(
    private storageService: StorageService,
  ) {
  }

  async init() {
    const clientId = await this.storageService.lfGet('clientId')

    const option: IClientOptions = {
      hostname: 'mp-mqtt-sg-01.mangopower.com',
      port: 8084,
      protocol: 'wss',
      path: '/mqtt',
      clean: true,
      clientId: `mp-iot-isp-${clientId}-` + Date.now(),
      username: 'mp_iot_isp_v3',
      password: 'eYhDoq4qe8J696oeE7fR',
    };

    this.client = mqtt.connect(option);

    await new Promise((resolve, reject) => {
      this.client.on('connect', async (p) => {
        await this.onConnect();
        resolve(1);
      });

      this.client.on('error', async (e) => {
        console.error(this.TAG, e);
        await this.onError();
        reject(e);
      });
    });
    this.client.on('message', async (topic, message) => {
      await this.onMessage(topic, message);
    });
  }

  async publish(topic: string, message: string | Buffer, opts: mqtt.IClientPublishOptions) {
    await this.client.publishAsync(topic, message, opts);
  }

  async subscribeDeviceEvent(sn: string) {
    const topic = this.deviceEventTopicPrefix + sn;
    if (this.topicMap.has(topic)) {
      return this.topicMap.get(topic);
    }
    const item = new DeviceSubscribeItem();
    item.sn = sn;
    this.topicMap.set(topic, item);
    await this.subscribe(topic, {qos: 1});
    return item;
  }

  async unSubscribeDeviceEvent(sn: string) {
    const topic = this.deviceEventTopicPrefix + sn;
    this.topicMap.delete(topic);
    await this.client.unsubscribeAsync(topic);
  }


  private async onError() {
    console.log(this.TAG, 'Mqtt client error!');
  }

  private async onMessage(topic: string, message: Buffer) {
    let msg: DeviceRemoteEvent = null;
    try {
      msg = JSON.parse(message.toString());
    } catch (e) {
      console.log(this.TAG, 'message parse error:', e);
    }
    if (this.topicMap.has(topic)) {
      const item = this.topicMap.get(topic);
      item.deviceEventSource.next(msg)
    }
  }

  private async onConnect() {
    console.log(this.TAG, 'Mqtt client connected!');
    await this.reSubscribeAll();
  }

  private async reSubscribeAll() {
    const topics = this.topicMap.keys();
    for (const topic of topics) {
      await this.subscribe(topic, {qos: 1});
    }
  }

  private async subscribe(topic: string | string[], opts: mqtt.IClientSubscribeOptions) {
    this.client.subscribe(topic, opts);
    console.log(this.TAG, `subscribe ${topic} success!`);
  }
}
